import React, { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Link } from "react-router-dom";
import { allMoviesAPI, nowPlayingMovies } from "../constants/api";
import { Play, Star, Film } from "lucide-react";
import MovieCardSkeleton from "./MovieCardSkeleton";

const Movies = () => {
  const [movies, setMovies] = useState([]);
  const [allMovies, setAllMovies] = useState([]);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchData = async (pageNumber, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    try {
      const data = await allMoviesAPI(pageNumber);
      return data?.results || [];
    } catch (error) {
      console.error(error);
      return [];
    } finally {
      if (isLoadMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  // Function to handle "Load More" button click
  const handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    // Save current scroll position
    const scrollPosition = window.scrollY;

    const nextPage = pages + 4;

    const dataForPage = await fetchData(nextPage, true);

    setAllMovies((prevMovies) => [
      ...(Array.isArray(prevMovies) ? prevMovies : []),
      ...dataForPage.map((movie) => ({ ...movie, page: nextPage })),
    ]);
    setPages(nextPage);
    
    // Restore scroll position after state update
    setTimeout(() => {
      window.scrollTo({
        top: scrollPosition,
        behavior: 'instant'
      });
    }, 0);
  };

  useEffect(() => {
    const fetchNowPlayingData = async () => {
      const data = await nowPlayingMovies();
      setMovies(data?.results || []);
    };

    fetchNowPlayingData();
  }, []);

  useEffect(() => {
    const fetchPopularMovies = async () => {
      const dataForPage1 = await fetchData(pages);
      const dataForPage2 = await fetchData(pages + 1);
      const dataForPage3 = await fetchData(pages + 2);
      setAllMovies([
        ...dataForPage1.map((movie) => ({ ...movie, page: pages })),
        ...dataForPage2.map((movie) => ({
          ...movie,
          page: pages + 1,
        })),
        ...dataForPage3.map((movie) => ({
          ...movie,
          page: pages + 2,
        })),
      ]);
    };

    fetchPopularMovies();
  }, []);

  // JSX code for the component
  return (
    <div className="bg-black padding pt-[4rem]">
      {/* Section Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-1 w-12 bg-red"></div>
          <span className="text-red mont font-semibold text-sm uppercase tracking-wider">
            Movies
          </span>
        </div>
        <h2 className="text-white mont font-bold lg:text-[42px] md:text-[36px] text-[28px] mb-1">
          Now Playing
        </h2>
        <p className="text-white/60 mont text-[15px]">
          Discover the latest movies in theaters
        </p>
      </div>

      {/* Modern Swiper component for "Now Playing" movies */}
      <div className="relative mb-12">
        <Swiper
          spaceBetween={20}
          slidesPerView={5}
          autoplay={{ 
            delay: 3000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          loop={movies.length > 5}
          navigation={true}
          pagination={{
            clickable: true,
            dynamicBullets: true,
          }}
          breakpoints={{
            230: {
              slidesPerView: 2,
              spaceBetween: 8,
            },
            480: {
              slidesPerView: 3,
              spaceBetween: 10,
            },
            640: {
              slidesPerView: 3,
              spaceBetween: 12,
            },
            768: {
              slidesPerView: 4,
              spaceBetween: 15,
            },
            1000: {
              slidesPerView: 5,
              spaceBetween: 18,
            },
            1280: {
              slidesPerView: 6,
              spaceBetween: 20,
            },
          }}
          modules={[Pagination, Autoplay, Navigation]}
          className="modern-swiper"
        >
          {movies?.filter(movie => movie.poster_path).map((movie) => (
            <SwiperSlide key={movie?.id} className="modern-movie-slide">
              <Link to={`/movies/${movie?.id}`} className="group">
                <div className="relative overflow-hidden rounded-lg bg-gray/30 movie-card-wrapper">
                  {/* Movie Poster */}
                  <div className="relative aspect-[2/3] overflow-hidden bg-gradient-to-br from-gray/40 to-gray/20">
                    <img
                      src={`https://image.tmdb.org/t/p/w500${movie.poster_path}`}
                      alt={movie?.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                    />
                    {/* Placeholder when image fails */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-gray/40 to-gray/20 p-4" style={{ display: 'none' }}>
                      <Film className="text-white/40 mb-2" size={48} />
                      <p className="text-white/60 mont text-xs text-center line-clamp-2">{movie?.title}</p>
                    </div>
                    
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    
                    {/* Play Button Overlay */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="bg-white/20 backdrop-blur-sm rounded-full p-4 transform scale-75 group-hover:scale-100 transition-transform duration-300">
                        <Play className="text-white" size={32} fill="white" />
                      </div>
                    </div>

                    {/* Rating Badge */}
                    <div className="absolute top-3 right-3 bg-black/70 backdrop-blur-sm rounded-full px-2.5 py-1 flex items-center gap-1">
                      <Star className="text-yellow-400" size={14} fill="#FBBF24" />
                      <span className="text-white mont text-xs font-semibold">
                        {movie?.vote_average?.toFixed(1)}
                      </span>
                    </div>
                  </div>

                  {/* Movie Info */}
                  <div className="p-3">
                    <h3 className="text-white mont font-semibold text-[15px] leading-tight line-clamp-2 group-hover:text-red transition-colors duration-300">
                      {movie?.title}
                    </h3>
                    {movie?.release_date && (
                      <p className="text-white/50 mont text-xs mt-1">
                        {new Date(movie.release_date).getFullYear()}
                      </p>
                    )}
                  </div>
                </div>
              </Link>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* "Popular Movies" section */}
      <div className="mb-8 mt-16">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-1 w-12 bg-red"></div>
          <span className="text-red mont font-semibold text-sm uppercase tracking-wider">
            Trending
          </span>
        </div>
        <h2 className="text-white mont font-bold lg:text-[42px] md:text-[36px] text-[28px] mb-1">
          Popular Movies
        </h2>
        <p className="text-white/60 mont text-[15px]">
          Explore the most popular movies
        </p>
      </div>

      <div className="grid lg:grid-cols-6 md:grid-cols-4 sm:grid-cols-3 grid-cols-2 gap-4 md:gap-6">
        {loading && allMovies.length === 0 ? (
          // Show skeletons on initial load
          Array.from({ length: 12 }).map((_, index) => (
            <MovieCardSkeleton key={index} />
          ))
        ) : (
          <>
            {allMovies?.filter(movie => movie.poster_path).map((movie) => (
              <Link
                key={movie?.id}
                to={`/movies/${movie?.id}`}
                className="group modern-movie-card"
              >
                <div className="relative overflow-hidden rounded-lg bg-gray/30 movie-card-wrapper">
                  {/* Movie Poster */}
                  <div className="relative aspect-[2/3] overflow-hidden bg-gradient-to-br from-gray/40 to-gray/20">
                    <img
                      src={`https://image.tmdb.org/t/p/w500${movie?.poster_path}`}
                      alt={movie?.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                    />
                    {/* Placeholder when image fails */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-gray/40 to-gray/20 p-4" style={{ display: 'none' }}>
                      <Film className="text-white/40 mb-2" size={40} />
                      <p className="text-white/60 mont text-xs text-center line-clamp-2">{movie?.title}</p>
                    </div>
                    
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    
                    {/* Play Button Overlay */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="bg-white/20 backdrop-blur-sm rounded-full p-3 transform scale-75 group-hover:scale-100 transition-transform duration-300">
                        <Play className="text-white" size={28} fill="white" />
                      </div>
                    </div>

                    {/* Rating Badge */}
                    <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1">
                      <Star className="text-yellow-400" size={12} fill="#FBBF24" />
                      <span className="text-white mont text-xs font-semibold">
                        {movie?.vote_average?.toFixed(1)}
                      </span>
                    </div>
                  </div>

                  {/* Movie Info */}
                  <div className="p-3">
                    <h3 className="text-white mont font-semibold text-[14px] leading-tight line-clamp-2 group-hover:text-red transition-colors duration-300">
                      {movie?.title}
                    </h3>
                    {movie?.release_date && (
                      <p className="text-white/50 mont text-xs mt-1">
                        {new Date(movie.release_date).getFullYear()}
                      </p>
                    )}
                  </div>
                </div>
              </Link>
            ))}
            
            {/* Show skeleton loaders when loading more */}
            {loadingMore && Array.from({ length: 6 }).map((_, index) => (
              <MovieCardSkeleton key={`skeleton-${index}`} />
            ))}
          </>
        )}
      </div>

      {/* Modern Load More Button */}
      <div className="flex justify-center mt-12">
        <button
          type="button"
          className="group relative overflow-hidden px-8 py-3 bg-red text-white mont font-semibold text-lg rounded-lg hover:bg-red/90 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-red/30"
          onClick={handleClick}
          disabled={loadingMore}
        >
          <span className="relative z-10 flex items-center gap-2">
            {loadingMore ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Loading...
              </>
            ) : (
              <>
                Load More Movies
                <Play className="ml-1" size={18} fill="white" />
              </>
            )}
          </span>
          <div className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transition-colors duration-300"></div>
        </button>
      </div>
    </div>
  );
};

export default Movies;
